import {
  PreviewCard,
  StandaloneLink,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@zeiss/beyond-online-react';
import { useBreakpoint } from '@zeiss/lib-helpers';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { OutletContextType, RemoveMemberModal } from './';
import { NOT_AVAILABLE } from 'src/constants';
import { TenantUserResponse } from 'src/types/rest/tenant';

interface MembersTableProps {
  canRemove?: boolean;
  data: TenantUserResponse[];
}

const PreviewCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StyledTableHeaderCell = styled(TableHeaderCell)`
  padding-block: 1rem 1.5rem;
  padding-inline: 0 1rem;
`;

export const MembersTable = ({ canRemove, data }: MembersTableProps) => {
  const { t } = useTranslation(['common']);
  const {
    data: { name: tenantName },
  } = useOutletContext<OutletContextType>();
  const isMinTablet = useBreakpoint('tablet');
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    React.useState(false);

  const [selectedMember, setSelectedMember] =
    React.useState<TenantUserResponse | null>(null);

  const showConfirmationModal = (member: TenantUserResponse) => {
    setSelectedMember(member);
    setIsConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };

  const displayValueWithFallback = (value?: string | null) => {
    return value ?? NOT_AVAILABLE;
  };

  return (
    <>
      {isMinTablet ? (
        <Table borderless borderStyle="collapse" hover tableLayout="fixed">
          <TableHead>
            <TableRow>
              <StyledTableHeaderCell>
                {t('common:table.header.memberSince')}
              </StyledTableHeaderCell>
              <StyledTableHeaderCell>
                {t('common:table.header.emailAddress')}
              </StyledTableHeaderCell>
              <StyledTableHeaderCell>
                {t('common:table.header.phoneNumber')}
              </StyledTableHeaderCell>
              {canRemove ? (
                <StyledTableHeaderCell>
                  {t('common:table.header.action_other')}
                </StyledTableHeaderCell>
              ) : null}
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((member) => {
              const { id, mail, memberSince, mobile } = member;
              return (
                <TableRow key={id}>
                  <TableCell>
                    {new Date(memberSince).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{displayValueWithFallback(mail)}</TableCell>
                  <TableCell>{displayValueWithFallback(mobile)}</TableCell>
                  {canRemove ? (
                    <TableCell>
                      <StandaloneLink
                        href=""
                        icon="PersonRemove"
                        iconPosition="right"
                        onClick={(e) => {
                          e.preventDefault();
                          showConfirmationModal(member);
                        }}
                      >
                        {t('common:action.remove')}
                      </StandaloneLink>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <PreviewCardContainer>
          {data.map((member) => {
            const { id, mail, memberSince, mobile } = member;
            const leadProp = mail ? mail : mobile ? mobile : id;
            const headline = leadProp;

            return (
              <PreviewCard
                chips={null}
                headline={headline}
                key={id}
                link={
                  canRemove
                    ? [
                        {
                          icon: 'PersonRemove',
                          label: t('common:action.remove'),
                          onClick: () => {
                            showConfirmationModal(member);
                          },
                        },
                      ]
                    : undefined
                }
                properties={[
                  {
                    key: t('common:table.header.memberSince'),
                    value: new Date(memberSince).toLocaleDateString(),
                  },
                  ...(leadProp !== mail
                    ? [
                        {
                          key: t('common:table.header.emailAddress'),
                          value: displayValueWithFallback(mail),
                        },
                      ]
                    : []),
                  ...(leadProp !== mobile
                    ? [
                        {
                          key: t('common:table.header.phoneNumber'),
                          value: displayValueWithFallback(mobile),
                        },
                      ]
                    : []),
                ]}
              />
            );
          })}
        </PreviewCardContainer>
      )}

      {selectedMember && (
        <RemoveMemberModal
          data={{ ...selectedMember, tenantName }}
          isOpen={isConfirmationModalOpen}
          onClose={closeConfirmationModal}
        />
      )}
    </>
  );
};
